import { ArrowRightLeft, Check, Link2, MapPin } from "lucide-react";
import type { ReactNode } from "react";

import { formatAmount } from "@/modules/dashboard/crypto-swaps/utils/cryptoSwapFormat";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { useCopyToClipboard } from "@/shared/hooks/useCopyToClipboard";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { getMccCategoryTKey } from "../constants/mccCategories.constants";
import {
  type CardTransaction,
  type CardTransactionCopTopUp,
  OperationType,
  TransactionStatus,
} from "../types/card-movements.types";
import { formatCopRate } from "../utils/cardCopTopUp";
import {
  formatExchangeRate,
  formatTransactionAmount,
  formatTransactionDateTime,
  getCountryName,
  getTransactionTypeMeta,
  resolveExchangeRate,
} from "../utils/cardTransactionDetailHelpers";
import { useCopTopUpFormat } from "./cop-top-up/copTopUpFormat";
import { CopTopUpRateRow } from "./cop-top-up/CopTopUpRateRow";

const STATUS_VARIANT: Record<TransactionStatus, "success" | "warning" | "destructive" | "secondary"> = {
  COMPLETED: "success",
  PENDING: "warning",
  FAILED: "destructive",
  REVERSED: "secondary",
};

const STATUS_TKEY: Record<TransactionStatus, string> = {
  COMPLETED: "movements_status_completed",
  PENDING: "movements_status_pending",
  FAILED: "movements_status_failed",
  REVERSED: "movements_status_reversed",
};

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <div className="mb-1.5 font-mono text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
    {children}
  </div>
);

interface DetailRowProps {
  label: ReactNode;
  value: ReactNode;
  mono?: boolean;
}

const DetailRow = ({ label, value, mono = false }: DetailRowProps) => {
  if (value == null || value === "") return null;
  return (
    <div className="flex items-center justify-between gap-3 border-t border-border p-3.5 first:border-t-0">
      <span className="shrink-0 text-[13px] font-medium text-muted-foreground">{label}</span>
      <span
        className={cn(
          "min-w-0 truncate text-right text-[13px] font-medium text-foreground",
          mono && "font-mono tabular-nums",
        )}
      >
        {value}
      </span>
    </div>
  );
};

/** A deposit a top-up from the COP wallet produced: what the dollar came to, then the money's trail. */
const CopTopUpSection = ({ topUp, locale }: { topUp: CardTransactionCopTopUp; locale: string }) => {
  const { t } = useTranslations("cards");
  const format = useCopTopUpFormat();
  const percent = topUp.deposit_fee_percent
    ? formatAmount(topUp.deposit_fee_percent, locale, { min: 0, max: 4 })
    : null;

  return (
    <div>
      <SectionTitle>{t("txn_type_deposit_cop")}</SectionTitle>
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <CopTopUpRateRow rates={topUp} locale={locale} />
        <DetailRow label={t("cop_top_up_row_paid")} value={format(topUp.send_amount, "COP")} mono />
        <DetailRow
          label={t("cop_top_up_row_price")}
          value={formatCopRate(topUp.cop_per_usdt, "USDT", locale)}
          mono
        />
        <DetailRow label={t("cop_top_up_row_sent_done")} value={format(topUp.deliver_amount, "USDT")} mono />
        {percent && topUp.deposit_fee_amount && (
          <DetailRow
            label={t("cop_top_up_row_fee", { percent })}
            value={`− ${format(topUp.deposit_fee_amount, "USD")}`}
            mono
          />
        )}
        <DetailRow
          label={t("cop_top_up_row_credited")}
          value={`${topUp.cop_per_usd_estimated ? "≈ " : ""}${format(topUp.credited_amount, "USD")}`}
          mono
        />
      </div>
    </div>
  );
};

export interface CardTransactionDetailBodyProps {
  detail: CardTransaction;
  locale: string;
  /** Absolute link that opens this movement on its card; copied by "Copy link". */
  link?: string;
  onClose: () => void;
}

export const CardTransactionDetailBody = ({
  detail,
  locale,
  link,
  onClose,
}: CardTransactionDetailBodyProps) => {
  const { t } = useTranslations("cards");
  const { copiedValue, copy } = useCopyToClipboard(t("txn_detail_copy_link_error"));
  const linkCopied = !!link && copiedValue === link;

  const typeMeta = getTransactionTypeMeta(detail.transaction_type, detail.top_up_source);
  const TypeIcon = typeMeta.icon;
  const typeLabel = t(typeMeta.tKey as never);

  const isCredit = detail.operation_type === OperationType.CREDIT;
  const isFailed = detail.status === TransactionStatus.FAILED;
  const isForeign = !!detail.currency && detail.currency !== "USD";
  const sign = isCredit ? "+" : "−";

  const statusVariant = STATUS_VARIANT[detail.status];
  const statusLabel = t(STATUS_TKEY[detail.status] as never);

  const countryName = getCountryName(detail.location?.country, locale);
  const hasLocation = !!detail.location?.city || !!countryName;
  const showWhere = !!detail.merchant_name || hasLocation || !!detail.merchant_terminal_id;

  const locationValue = [detail.location?.city, countryName].filter(Boolean).join(", ");

  const categoryCode = detail.merchant_category_code;
  const categoryTKey = getMccCategoryTKey(categoryCode);
  const categoryLabel = categoryTKey ? t(categoryTKey as never) : null;
  const categoryValue = categoryCode
    ? categoryLabel
      ? `${categoryLabel} · ${categoryCode}`
      : categoryCode
    : null;

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex-1 space-y-4 pb-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <span
            className={cn(
              "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)]",
              typeMeta.iconWrapper,
            )}
            aria-hidden="true"
          >
            <TypeIcon className="h-[18px] w-[18px]" />
          </span>
          <div className="min-w-0">
            <div className="truncate text-[15px] font-semibold text-foreground">{typeLabel}</div>
            <div className="text-[11.5px] text-muted-foreground">{t("txn_detail_subtitle")}</div>
          </div>
        </div>

        {/* Hero amount */}
        <div className="rounded-lg border border-border bg-muted/40 px-4 py-6 text-center">
          <div
            className={cn(
              "font-mono text-[34px] font-bold leading-none tabular-nums tracking-tight",
              isFailed
                ? "text-muted-foreground line-through"
                : isCredit
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-foreground",
            )}
          >
            {sign}
            {formatTransactionAmount(Math.abs(detail.amount), detail.currency, locale)}
            <span className="ml-1.5 text-[15px] font-medium text-muted-foreground">{detail.currency}</span>
          </div>
          {isForeign && detail.usd_amount != null && (
            <div className="mt-2 font-mono text-[13px] text-muted-foreground">
              ≈ {sign}
              {formatTransactionAmount(Math.abs(detail.usd_amount), "USD", locale)} USD
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
            <ColocaBadge variant={statusVariant} className="text-[10px]">
              {statusLabel}
            </ColocaBadge>
            <span className="text-[12px] text-muted-foreground">
              {formatTransactionDateTime(detail.created_at, locale)}
            </span>
          </div>
        </div>

        {/* Movimiento */}
        <div>
          <SectionTitle>{t("txn_detail_section_movement")}</SectionTitle>
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <DetailRow label={t("txn_detail_field_type")} value={typeLabel} />
            <DetailRow
              label={t("txn_detail_field_operation")}
              value={isCredit ? t("txn_detail_operation_credit") : t("txn_detail_operation_debit")}
            />
            <DetailRow label={t("txn_detail_field_description")} value={detail.description} />
          </div>
        </div>

        {/* Realizado por */}
        {detail.created_by && (
          <div>
            <SectionTitle>{t("txn_detail_section_performed_by")}</SectionTitle>
            <div className="overflow-hidden rounded-lg border border-border bg-card">
              <DetailRow
                label={t("txn_detail_field_performed_by_name")}
                value={detail.created_by.full_name}
              />
              <DetailRow
                label={t("txn_detail_field_performed_by_email")}
                value={detail.created_by.email}
                mono
              />
            </div>
          </div>
        )}

        {/* Dónde */}
        {showWhere && (
          <div>
            <SectionTitle>{t("txn_detail_section_where")}</SectionTitle>
            <div className="overflow-hidden rounded-lg border border-border bg-card">
              <DetailRow label={t("txn_detail_field_merchant")} value={detail.merchant_name} />
              <DetailRow label={t("txn_detail_field_category")} value={categoryValue} mono />
              {hasLocation && (
                <DetailRow
                  label={t("txn_detail_field_location")}
                  value={
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      {locationValue}
                    </span>
                  }
                />
              )}
              <DetailRow label={t("txn_detail_field_terminal")} value={detail.merchant_terminal_id} mono />
            </div>
          </div>
        )}

        {detail.cop_top_up && <CopTopUpSection topUp={detail.cop_top_up} locale={locale} />}

        {/* Conversión de moneda */}
        {isForeign && (
          <div>
            <SectionTitle>{t("txn_detail_section_conversion")}</SectionTitle>
            <div className="overflow-hidden rounded-lg border border-border bg-card">
              {detail.exchange_rate != null && (
                <DetailRow
                  label={
                    <span className="inline-flex items-center gap-1.5">
                      <ArrowRightLeft className="h-3.5 w-3.5" aria-hidden="true" />
                      {t("txn_detail_field_exchange_rate")}
                    </span>
                  }
                  value={formatExchangeRate(
                    resolveExchangeRate(detail.amount, detail.usd_amount, detail.exchange_rate),
                    detail.currency,
                    locale,
                  )}
                  mono
                />
              )}
              {detail.spot_variation_percentage != null && (
                <DetailRow
                  label={t("txn_detail_field_spot_variation")}
                  value={
                    <span
                      className={cn(
                        detail.spot_variation_percentage >= 0
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-destructive",
                      )}
                    >
                      {detail.spot_variation_percentage >= 0 ? "↑" : "↓"}{" "}
                      {Math.abs(detail.spot_variation_percentage).toFixed(2)}%
                    </span>
                  }
                  mono
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Sticky action row — ColocaDrawer pins the last child to the bottom. */}
      <div className="relative z-20 flex items-center justify-between gap-2 border-t border-dashed border-border pt-3">
        {link ? (
          <ColocaButton
            variant="default"
            size="sm"
            icon={linkCopied ? Check : Link2}
            onClick={() => void copy(link, t("txn_detail_link_copied"))}
          >
            {linkCopied ? t("txn_detail_link_copied") : t("txn_detail_copy_link")}
          </ColocaButton>
        ) : (
          <span />
        )}
        <ColocaButton variant="secondary" size="sm" onClick={onClose}>
          {t("txn_detail_close")}
        </ColocaButton>
      </div>
    </div>
  );
};
